
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import { InputType } from '@/enums/InputType';
import { VenueStoreStatus } from '@/enums/SmoothrPay';
import Countries from '@/mixins/Countries';
import { Venue } from '@/interfaces/models/Venue';
import {
  CreateVenueStoreRequest,
  MerchantConfiguration,
  SplitConfiguration,
  VenueStore,
} from '@/interfaces/models/SmoothrPay';

/**
 * The venues this merchant serves, one Adyen store each. The address, the
 * shopper statement (Adyen caps it at 22 characters) and optionally a split
 * configuration profile are all needed at creation time.
 */
@Component({ components: { VFormBuilder } })
export default class VenueStores extends mixins(Countries) {
  @Prop({ type: Object, required: true }) public configuration!: MerchantConfiguration;
  @Prop({ type: Array, default: () => [] }) public stores!: VenueStore[];
  @Prop({ type: Array, default: () => [] }) public venues!: Venue[];
  @Prop({ type: Array, default: () => [] }) public splitConfigurations!: SplitConfiguration[];
  @Prop({ type: Boolean, default: false }) public canManage!: boolean;
  @Prop({ type: Boolean, default: false }) public saving!: boolean;

  public $refs!: { form: InstanceType<typeof VFormBuilder> & { form: any; validate: () => Promise<boolean> } };

  get closedStatus(): VenueStoreStatus {
    return VenueStoreStatus.CLOSED;
  }

  get hasBusinessLine(): boolean {
    return (this.configuration.businessLines || []).some((line) => !!line.id);
  }

  get initialValues() {
    return { country: 'de' };
  }

  get assignableVenues() {
    const taken = this.stores.map((store: VenueStore) => store.venue);
    return this.venues
      .filter((venue: Venue) => taken.indexOf(venue._id) === -1)
      .map((venue: Venue) => ({ text: venue.name, value: venue._id }));
  }

  get splitItems() {
    return this.splitConfigurations.map((split: SplitConfiguration) => ({
      text: split.description || split.splitConfigurationId,
      value: split.splitConfigurationId,
    }));
  }

  get balanceAccountItems() {
    return (this.configuration.balanceAccounts || [])
      .filter((account) => !!account.id)
      .map((account) => ({ text: account.description || account.id, value: account.id }));
  }

  get items() {
    return [
      {
        name: 'venue',
        type: InputType.Select,
        label: 'smoothrPay.stores.venue',
        items: this.assignableVenues,
        rules: 'required',
      },
      { name: 'description', type: InputType.Text, label: 'smoothrPay.fields.description', rules: 'required' },
      {
        name: 'shopperStatement',
        type: InputType.Text,
        label: 'smoothrPay.fields.shopperStatement',
        hint: 'smoothrPay.hints.shopperStatement',
        rules: 'required|max:22',
      },
      { name: 'phoneNumber', type: InputType.Text, label: 'smoothrPay.fields.phoneNumber', rules: 'required' },
      { name: 'line1', type: InputType.Text, label: 'smoothrPay.fields.line1', rules: 'required' },
      { name: 'postalCode', type: InputType.Text, label: 'smoothrPay.fields.postalCode', rules: 'required' },
      { name: 'city', type: InputType.Text, label: 'smoothrPay.fields.city', rules: 'required' },
      {
        name: 'country',
        type: InputType.Select,
        label: 'smoothrPay.fields.country',
        items: this.countries,
        rules: 'required',
      },
      {
        name: 'splitConfigurationId',
        type: InputType.Select,
        label: 'smoothrPay.fields.splitConfiguration',
        items: this.splitItems,
        visible: this.splitItems.length > 0,
      },
      {
        name: 'balanceAccountId',
        type: InputType.Select,
        label: 'smoothrPay.fields.balanceAccount',
        items: this.balanceAccountItems,
        // Only read together with a split profile, and pointless with one account.
        visible: this.balanceAccountItems.length > 1,
      },
    ];
  }

  public storeColor(store: VenueStore): string {
    switch (store.status) {
      case VenueStoreStatus.ACTIVE:
        return 'success';
      case VenueStoreStatus.PENDING:
        return 'info';
      case VenueStoreStatus.CLOSED:
        return 'error';
      default:
        return 'grey';
    }
  }

  public venueName(id: string): string {
    const venue = this.venues.find((item: Venue) => item._id === id);
    return venue ? venue.name : id;
  }

  public async assign() {
    const valid = await this.$refs.form.validate();
    if (!valid) {
      return;
    }
    const form: any = this.$refs.form.form;
    const payload: CreateVenueStoreRequest = {
      venue: form.venue,
      merchantConfigurationId: this.configuration._id,
      description: form.description,
      phoneNumber: form.phoneNumber,
      shopperStatement: form.shopperStatement,
      address: {
        country: form.country,
        line1: form.line1,
        postalCode: form.postalCode,
        city: form.city,
      },
    };
    if (form.splitConfigurationId) {
      payload.splitConfigurationId = form.splitConfigurationId;
      if (form.balanceAccountId) {
        payload.balanceAccountId = form.balanceAccountId;
      }
    }
    this.$emit('assign', payload);
  }
}
